<script lang="ts">
  import type { Snippet } from 'svelte'

  interface Props {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    href?: string
    type?: 'button' | 'submit'
    disabled?: boolean
    loading?: boolean
    /** Renders as a full-width block. */
    block?: boolean
    external?: boolean
    onclick?: (event: MouseEvent) => void
    children: Snippet
  }

  let {
    variant = 'primary',
    size = 'md',
    href,
    type = 'button',
    disabled = false,
    loading = false,
    block = false,
    external = false,
    onclick,
    children,
  }: Props = $props()

  // A loading button must stay in the tab order and keep its width, so it is
  // disabled rather than replaced — swapping it for a spinner moves focus and
  // makes the layout jump at the exact moment the user is waiting.
  const inert = $derived(disabled || loading)
</script>

{#if href}
  <a
    {href}
    class="btn btn--{variant} btn--{size}"
    class:btn--block={block}
    class:is-disabled={inert}
    aria-disabled={inert || undefined}
    target={external ? '_blank' : undefined}
    rel={external ? 'noopener noreferrer' : undefined}
    data-external={external ? '' : undefined}
    {onclick}
  >
    {@render children()}
  </a>
{:else}
  <button
    {type}
    class="btn btn--{variant} btn--{size}"
    class:btn--block={block}
    disabled={inert}
    aria-busy={loading || undefined}
    {onclick}
  >
    {#if loading}<span class="spinner" aria-hidden="true"></span>{/if}
    {@render children()}
  </button>
{/if}

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border: 1px solid transparent;
    border-radius: var(--radius);
    font-weight: 560;
    letter-spacing: -0.008em;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease,
                color 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease;
  }
  .btn:hover { text-decoration: none; }
  .btn:active:not(.is-disabled):not(:disabled) { transform: translateY(0.5px); }
  .btn:disabled, .btn.is-disabled { opacity: 0.55; cursor: not-allowed; pointer-events: none; }

  .btn--sm { padding: 0.36rem 0.75rem; font-size: 0.845rem; }
  .btn--md { padding: 0.55rem 1.1rem; font-size: 0.925rem; }
  .btn--lg { padding: 0.76rem 1.6rem; font-size: 1rem; }

  .btn--primary {
    background: var(--accent);
    color: #fff;
    box-shadow: var(--shadow-sm);
  }
  .btn--primary:hover { background: var(--accent-hover); box-shadow: var(--shadow-md); }

  .btn--secondary {
    background: var(--surface);
    color: var(--text);
    border-color: var(--border-strong);
  }
  .btn--secondary:hover { background: var(--surface-hover); border-color: var(--text-faint); }

  .btn--ghost { background: transparent; color: var(--text-muted); }
  .btn--ghost:hover { background: var(--surface-hover); color: var(--text); }

  .btn--danger { background: transparent; color: var(--danger); border-color: currentColor; }
  .btn--danger:hover { background: var(--danger-soft); }

  .btn--block { display: flex; width: 100%; }

  .spinner {
    width: 0.9em;
    height: 0.9em;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    flex: none;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
